import { Eye, Loader2, Pencil, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import { cn } from '@/shared/lib/cn';
import { Button } from './button';

/**
 * Generic ABM / master-data table used across the Configuración module.
 * Each row is identified by an opaque `id` string (SharePoint item id).
 * Column content can be any ReactNode — keep the values short; for rich
 * formatting (badges, dates) pass the formatted node.
 */

export type CrudColumn<T> = {
  header: string;
  /** Optional width: tailwind class, e.g. 'w-24'. */
  width?: string;
  /** Align right for actions column (default is left). */
  align?: 'left' | 'right';
  /**
   * Mobile-card behavior. `'hidden'` skips the column in the stacked
   * card layout (use for columns that are redundant or noisy on small
   * screens, like long internal IDs). Default is to show the column
   * with `header` as its label.
   */
  mobile?: 'hidden';
  render: (row: T) => ReactNode;
};

export type CrudTableProps<T extends { id: string }> = {
  rows: T[];
  columns: CrudColumn<T>[];
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  emptyMessage?: string;
  onEdit?: (row: T) => void;
  onView?: (row: T) => void;
  onDelete?: (row: T) => void;
  /** Per-row gate for the edit action. Return false to disable (still
   *  shown, with an optional tooltip from `editDisabledTitle`). */
  canEdit?: (row: T) => boolean;
  editDisabledTitle?: (row: T) => string | undefined;
  /** Per-row gate for the delete action. Return false to disable (still
   *  shown, with an optional tooltip from `deleteDisabledTitle`). */
  canDelete?: (row: T) => boolean;
  deleteDisabledTitle?: (row: T) => string | undefined;
  /**
   * Per-row SHOW/HIDE gate for edit+delete that OVERRIDES the global read-only
   * role gate. Return true to show the write actions for that row even for a
   * read-only role (e.g. a marketing user on its OWN venta). Undefined → the
   * role-based `isReadOnly` gate decides. Distinct from `canEdit`/`canDelete`,
   * which only DISABLE an already-shown button.
   */
  canRowWrite?: (row: T) => boolean;
  /** Hide all action buttons (for read-only entities like Parametros). */
  hideActions?: boolean;
  /** Show an updating indicator below the table. */
  isBackgroundFetching?: boolean;
};

export function CrudTable<T extends { id: string }>({
  rows,
  columns,
  loading,
  error,
  onRetry,
  emptyMessage = 'Sin resultados.',
  onEdit,
  onView,
  onDelete,
  canEdit,
  editDisabledTitle,
  canDelete,
  deleteDisabledTitle,
  canRowWrite,
  hideActions,
  isBackgroundFetching,
}: CrudTableProps<T>) {
  // Visitante is universally read-only — suppress edit/delete affordances
  // but keep the View (eye) action so they can still drill into details.
  // Backend already returns 403 on writes; this is the matching UI gate.
  const isReadOnly = useIsReadOnly();
  // Per-row write override (canRowWrite) beats the global read-only gate — used
  // when a read-only role has per-record access (marketing on its OWN venta).
  const rowWritable = (row: T) => (canRowWrite ? canRowWrite(row) : !isReadOnly);
  const showActionsColumn =
    !hideActions &&
    (canRowWrite
      ? Boolean(onView || onEdit || onDelete)
      : isReadOnly
        ? Boolean(onView)
        : true);
  const totalCols = columns.length + (showActionsColumn ? 1 : 0);

  const renderActions = (row: T) => {
    if (!showActionsColumn) return null;
    return (
      <div className="inline-flex items-center gap-1">
        {onEdit && rowWritable(row) && (() => {
          const allowed = canEdit ? canEdit(row) : true;
          return (
            <IconAction
              label={
                allowed ? 'Editar' : editDisabledTitle?.(row) ?? 'No se puede editar'
              }
              disabled={!allowed}
              onClick={() => allowed && onEdit(row)}
            >
              <Pencil className="h-4 w-4" aria-hidden />
            </IconAction>
          );
        })()}
        {onView && (
          <IconAction label="Ver" onClick={() => onView(row)}>
            <Eye className="h-4 w-4" aria-hidden />
          </IconAction>
        )}
        {onDelete && rowWritable(row) && (() => {
          const allowed = canDelete ? canDelete(row) : true;
          return (
            <IconAction
              label={
                allowed ? 'Eliminar' : deleteDisabledTitle?.(row) ?? 'No se puede eliminar'
              }
              tone="destructive"
              disabled={!allowed}
              onClick={() => allowed && onDelete(row)}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </IconAction>
          );
        })()}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border bg-card">
      {/* Desktop / tablet table */}
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.header}
                  className={cn(
                    'px-4 py-3 font-semibold',
                    c.align === 'right' && 'text-right',
                    c.width,
                  )}
                >
                  {c.header}
                </th>
              ))}
              {showActionsColumn && (
                <th className="w-24 px-4 py-3 text-right font-semibold">Acciones</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <StatusRow cols={totalCols}>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Cargando…
              </StatusRow>
            ) : error ? (
              <StatusRow cols={totalCols}>
                <span className="text-destructive">No se pudieron cargar los datos.</span>
                {onRetry && (
                  <Button variant="outline" size="sm" onClick={onRetry}>
                    Reintentar
                  </Button>
                )}
              </StatusRow>
            ) : rows.length === 0 ? (
              <StatusRow cols={totalCols}>{emptyMessage}</StatusRow>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="transition-colors hover:bg-muted/30">
                  {columns.map((c) => (
                    <td
                      key={c.header}
                      className={cn(
                        'px-4 py-3 align-middle',
                        c.align === 'right' && 'text-right',
                      )}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                  {showActionsColumn && (
                    <td className="px-4 py-3 text-right align-middle">
                      {renderActions(row)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card view — stacks all columns as label/value pairs.
       *  Generic by design: each consumer's `columns[].header` doubles as
       *  the field label; columns hide via `mobile: 'hidden'`. Action
       *  buttons go at the bottom of each card.
       */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Cargando…
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-destructive">
            <span>No se pudieron cargar los datos.</span>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                Reintentar
              </Button>
            )}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">{emptyMessage}</div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((row) => (
              <li key={row.id} className="flex flex-col gap-2 px-4 py-3">
                <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm">
                  {columns
                    .filter((c) => c.mobile !== 'hidden')
                    .map((c) => (
                      <div key={c.header} className="contents">
                        <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                          {c.header}
                        </dt>
                        <dd className="min-w-0 break-words">{c.render(row)}</dd>
                      </div>
                    ))}
                </dl>
                {showActionsColumn && (
                  <div className="flex items-center justify-end pt-1">{renderActions(row)}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {isBackgroundFetching && !loading && (
        <div className="flex shrink-0 items-center justify-center gap-2 border-t px-4 py-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Actualizando…
        </div>
      )}
    </div>
  );
}

function StatusRow({ cols, children }: { cols: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function IconAction({
  label,
  onClick,
  tone,
  disabled = false,
  children,
}: {
  label: string;
  onClick: () => void;
  tone?: 'destructive';
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        tone === 'destructive'
          ? 'border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20'
          : 'border border-border text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
